import React, { useEffect, useState } from "react";
import { UserPlus, RefreshCw, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Button, Badge, EmptyState, Card, Avatar } from "@/components/ui-em";
import InviteModal from "@/components/InviteModal";

export default function InvitePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [resendModal, setResendModal] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/team/my-invites").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const resend = async (id, name) => {
    try {
      const { data } = await api.post(`/users/${id}/resend-invitation`);
      setResendModal({
        name,
        invitation_url: data.invitation_url,
      });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Invitation link copied");
    } catch {
      toast.error("Could not copy — select and copy manually");
    }
  };

  const pending = items.filter((i) => i.status === "pending");
  const active = items.filter((i) => i.status === "active");

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Team"
        title="Invite your network"
        description="Grow your team. Invite leaders from your network — they set their own password via a private link."
        actions={<Button icon={UserPlus} onClick={() => setShowInvite(true)} size="lg" data-testid="invite-page-btn">Invite a leader</Button>}
      />

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading...</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Users}
          title="You haven't invited anyone yet"
          description="Duplication starts here. Invite the first leader from your network and grow together."
          actionLabel="Send your first invitation"
          onAction={() => setShowInvite(true)}
        />
      ) : (
        <div className="space-y-8">
          {pending.length > 0 && (
            <Card>
              <div className="p-6 pb-4 border-b border-white/5 flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">Pending invitations</p>
                  <h2 className="font-display text-lg mt-1">{pending.length} awaiting activation</h2>
                </div>
              </div>
              <div className="divide-y divide-white/5">
                {pending.map((u) => (
                  <div key={u.id} className="px-6 py-4 flex items-center gap-4">
                    <Avatar name={u.name} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white truncate">{u.name}</div>
                      <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                    </div>
                    <Badge variant="warn" size="sm" uppercase>Pending</Badge>
                    <button
                      onClick={() => resend(u.id, u.name)}
                      data-testid={`resend-${u.id}`}
                      className="p-2 rounded-md text-zinc-500 hover:text-gold hover:bg-gold/5"
                      style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
                      title="Get new link"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {active.length > 0 && (
            <Card>
              <div className="p-6 pb-4 border-b border-white/5">
                <p className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">Your team</p>
                <h2 className="font-display text-lg mt-1">{active.length} active leader{active.length === 1 ? "" : "s"}</h2>
              </div>
              <div className="divide-y divide-white/5">
                {active.map((u) => (
                  <div key={u.id} className="px-6 py-4 flex items-center gap-4">
                    <Avatar name={u.name} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white truncate">{u.name}</div>
                      <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                    </div>
                    <Badge variant="success" size="sm" uppercase>Active</Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      <InviteModal open={showInvite} onClose={() => setShowInvite(false)} onCreated={load} />

      {resendModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-ink-0/80 backdrop-blur-md">
          <div className="em-card p-6 sm:p-8 w-full max-w-xl" style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)" }}>
            <h3 className="font-display text-xl mb-2">New link for <span className="text-gold">{resendModal.name}</span></h3>
            <p className="text-sm text-zinc-500 mb-6">Old links are now invalid. Share this fresh link — valid for 7 days.</p>
            <div className="em-surface rounded-xl p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="flex-1 min-w-0 text-xs text-zinc-300 font-mono truncate select-all">{resendModal.invitation_url}</div>
              <button onClick={() => copyLink(resendModal.invitation_url)} className="shrink-0 bg-gold text-ink-0 font-semibold rounded-lg px-4 py-2.5 text-sm hover:bg-gold-hover" style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}>
                Copy link
              </button>
            </div>
            <div className="mt-6 flex justify-end">
              <Button variant="secondary" onClick={() => setResendModal(null)}>Done</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
